import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  OffsetTeaserContentBoxAlignment,
  OffsetTeaserImageSize,
  OffsetTeaserSchemeDefinition,
  OffsetTeaserSchemeName
} from "./constants/offset-teaser-types";
import {
  OFFSET_TEASER_CONTENT_BOX_ALIGNMENT_LEFT,
  OFFSET_TEASER_IMAGE_SIZE_MEDIUM,
  OFFSET_TEASER_SCHEME_CONFIG,
  OFFSET_TEASER_SCHEME_MAP
} from "./constants/offset-teaser-constants";
import { OffsetTeaserSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import { HEADLINE_TAG_H2 } from "../headline/constants/headline-constants";
// import { InitLogging } from "../../decorators/init-logging";
import { getParentEonUiElement } from "../../utils/dom-helper";

/**
 * @slot image - Holds a single eon-ui-image element
 * @slot content - Holds any number of HTML elements
 * @slot cta - Holds a link or button component
 */
@Component({
  tag: "eon-ui-offset-teaser",
  styleUrl: "offset-teaser.scss",
  assetsDirs: ["assets-offset-teaser"],
  shadow: true
})
export class OffsetTeaser
  implements
    ColorableComponent<OffsetTeaserSchemeConfiguration<OffsetTeaserSchemeDefinition>>,
    ContainerBreakpointComponent {
  // @InitLogging() sentryTransaction;
  schemeConfig: OffsetTeaserSchemeConfiguration<OffsetTeaserSchemeDefinition> = OFFSET_TEASER_SCHEME_CONFIG;
  schemeMap = OFFSET_TEASER_SCHEME_MAP;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  hasCta: boolean = false;

  @Element() hostElement: HTMLEonUiOffsetTeaserElement;

  @State() hasImage: boolean;
  @State() seoHeadlineTag: string;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: OffsetTeaserSchemeDefinition;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: OffsetTeaserSchemeName;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Defines the size of the image.
   */
  @Prop({ reflect: true, mutable: true }) imageSize: OffsetTeaserImageSize = OFFSET_TEASER_IMAGE_SIZE_MEDIUM;

  /**
   * Defines the alignment of the content box.
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop({ reflect: true, mutable: true })
  contentBoxAlignment: OffsetTeaserContentBoxAlignment = OFFSET_TEASER_CONTENT_BOX_ALIGNMENT_LEFT;

  /**
   * The text of the eon-ui-headline inside the teaser.
   */
  @Prop() headlineText: string;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline,<br>
   *   the HTML-Tag for the subheadline is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    Headline is H2, then subheadline is H3, Headline is H3, then subheadline is H5, and so on.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * The text of the eon-ui-headline inside the teaser.
   */
  @Prop() subheadlineText: string;

  /**
   * Raised when the offsetTeaser has been loaded.
   */
  @Event() offsetTeaserLoad: EventEmitter;

  /**
   * Raised when the CTA in offsetTeaser has been clicked.
   */
  @Event() offsetTeaserClick: EventEmitter;

  componentWillLoad() {
    this.applyScheme();
    this.initContainerBreakpoints();
    this.manageInternalImage();
    this.hasCta = this.hostElement.querySelectorAll("[slot='cta']").length > 0;
  }

  componentDidLoad() {
    this.setCssVariables();
    this.offsetTeaserLoad.emit();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  private manageInternalImage() {
    let image = this.hostElement.querySelector('[slot="image"]') as HTMLEonUiImageElement;
    if (image && this.hostElement === getParentEonUiElement(image)) {
      this.hasImage = !!image;
      if (this.hasImage) image.useAsBackground = true;
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H2);
  }

  @Listen("buttonClick")
  @Listen("linkClick")
  handleCTAOnClick(event) {
    this.offsetTeaserClick.emit(event);
  }

  render() {
    return (
      <Host>
        {this.hasImage ? (
          <div
            class={pfx({
              "offset-teaser-wrapper": true,
              [`image-size-${this.imageSize}`]: !!this.imageSize,
              [`content-box-alignment-${this.contentBoxAlignment}`]: !!this.contentBoxAlignment
            })}
          >
            <div class={pfx("offset-teaser-image-wrapper")}>
              <div class={pfx("offset-teaser-image-inner")}>
                <slot name="image"></slot>
              </div>
            </div>
            <div class={pfx({ "offset-teaser-content-box-wrapper": true, overlap: true })}>
              <div
                class={pfx({
                  "offset-teaser-content-box-inner": true,
                  gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
                })}
              >
                <div class={pfx("offset-teaser-content")}>
                  {this.headlineText && (
                    <eon-ui-headline
                      class={pfx("offset-teaser-headline")}
                      headline-html-tag={this.seoHeadlineTag}
                      size="h2"
                      size-container-based={true}
                      text={this.headlineText}
                    ></eon-ui-headline>
                  )}
                  {this.subheadlineText && (
                    <eon-ui-headline
                      class={pfx("offset-teaser-subheadline")}
                      headline-html-tag="p"
                      size="h4"
                      size-container-based={true}
                      text={this.subheadlineText}
                    ></eon-ui-headline>
                  )}
                  <slot name="content"></slot>
                </div>
                {this.hasCta && (
                  <div class={pfx("offset-teaser-cta")}>
                    <slot name="cta"></slot>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            class={pfx({
              "offset-teaser-wrapper": true,
              [`content-box-alignment-${this.contentBoxAlignment}`]: !!this.contentBoxAlignment
            })}
          >
            <div class={pfx({ "offset-teaser-content-box-wrapper": true, "without-image": true })}>
              <div
                class={pfx({
                  "offset-teaser-content-box-inner": true,
                  split: true,
                  gradient: this.activeScheme.gradientColor // E.ON specific. Only set if gradientColor variable of a scheme is defined.
                })}
              >
                <div class={pfx("offset-teaser-content-box-inner-split")}>
                  {this.headlineText && (
                    <eon-ui-headline
                      class={pfx("offset-teaser-headline")}
                      headline-html-tag={this.seoHeadlineTag}
                      size="h2"
                      text={this.headlineText}
                    ></eon-ui-headline>
                  )}
                </div>
                <div class={pfx("offset-teaser-content-box-inner-split")}>
                  <div class={pfx("offset-teaser-content")}>
                    {this.subheadlineText && (
                      <eon-ui-headline
                        class={pfx("offset-teaser-subheadline")}
                        headline-html-tag="p"
                        size="h4"
                        text={this.subheadlineText}
                      ></eon-ui-headline>
                    )}
                    <slot name="content"></slot>
                  </div>
                  {this.hasCta && (
                    <div class={pfx("offset-teaser-cta")}>
                      <slot name="cta"></slot>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Host>
    );
  }
}
